// ═══════════════════════════════════════════════════════════════════════════════
// Grouped usage region dropdown (register + settings)
// ═══════════════════════════════════════════════════════════════════════════════

import type { ChangeEvent } from 'react';
import { USAGE_REGION_GROUPS, DEFAULT_USAGE_REGION, type UsageRegion } from '@fdh/shared';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  usageRegionGroupLabelKey,
  usageRegionLabelKey,
} from '../../i18n/usageRegionMessages';

type UsageRegionSelectProps = {
  id: string;
  value: UsageRegion;
  onChange: (value: UsageRegion) => void;
  label: string;
  disabled?: boolean;
  /** `settings` matches SettingsPage field labels; `register` matches registration wizard. */
  variant?: 'register' | 'settings';
};

const VARIANT_LABEL_CLASS: Record<'register' | 'settings', string> = {
  register: 'block text-sm font-semibold text-gray-800 dark:text-gray-100',
  settings: 'block text-sm font-medium text-gray-700 dark:text-gray-300',
};

const VARIANT_SELECT_CLASS: Record<'register' | 'settings', string> = {
  register:
    'mt-2 w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 text-gray-900 shadow-sm focus:border-primary-600 focus:ring-2 focus:ring-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-900 dark:text-white',
  settings:
    'mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100',
};

export const UsageRegionSelect = ({
  id,
  value,
  onChange,
  label,
  disabled = false,
  variant = 'register',
}: UsageRegionSelectProps) => {
  const { t } = useLanguage();

  const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    onChange(e.target.value as UsageRegion);
  };

  return (
    <div>
      <label htmlFor={id} className={VARIANT_LABEL_CLASS[variant]}>
        {label}
      </label>
      <select
        id={id}
        value={value || DEFAULT_USAGE_REGION}
        onChange={handleChange}
        disabled={disabled}
        className={VARIANT_SELECT_CLASS[variant]}
      >
        {USAGE_REGION_GROUPS.map(({ group, regions }) => (
          <optgroup key={group} label={t(usageRegionGroupLabelKey(group))}>
            {regions.map((region) => (
              <option key={region} value={region}>
                {t(usageRegionLabelKey(region))}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </div>
  );
};
